import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useMotion, EASE_SNAP, EASE_POP } from './motion';

interface PressableProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  children: ReactNode;
}

/**
 * Pressable — press-down feedback on anything tappable: quick scale-in on
 * pointerdown, springy return on release. This is the single biggest
 * contributor to the "app" feel; Pear's CSS-only version had
 * `.pear-btn:active { transform: translateY(0) }`, which merely undid the
 * hover lift, so taps registered nothing.
 *
 * Always renders a real <button>: keyboard-accessible by default.
 */
export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
  ({ children, className, style, disabled, ...rest }, ref) => {
    const [pressed, setPressed] = useState(false);
    const { reduced } = useMotion();

    const pressScale = reduced ? 1 : 0.95;
    const pressDur = reduced ? 0 : 90;
    const releaseDur = reduced ? 0 : 260;

    return (
      <button
        ref={ref}
        className={className}
        disabled={disabled}
        onPointerDown={(e) => {
          setPressed(true);
          rest.onPointerDown?.(e);
        }}
        onPointerUp={(e) => {
          setPressed(false);
          rest.onPointerUp?.(e);
        }}
        onPointerLeave={(e) => {
          setPressed(false);
          rest.onPointerLeave?.(e);
        }}
        onPointerCancel={(e) => {
          setPressed(false);
          rest.onPointerCancel?.(e);
        }}
        style={{
          // Button resets FIRST so callers can style freely; motion-critical
          // values last so they always win over caller styles.
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          font: 'inherit',
          color: 'inherit',
          ...style,
          transform: pressed ? `scale(${pressScale})` : 'scale(1)',
          transition: `transform ${pressed ? pressDur : releaseDur}ms ${pressed ? EASE_SNAP : EASE_POP}`,
          cursor: disabled ? 'default' : 'pointer',
          touchAction: 'manipulation',
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

Pressable.displayName = 'Pressable';
